import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  Activity, 
  FileText, 
  X, 
  Search,
  Filter,
  Download,
  Eye,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Calendar,
  AlertCircle,
  CreditCard,
  Wallet,
  TrendingUpDown
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = '';

const EnhancedTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };
  
  // Filter states
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState({
    total: 0,
    deposits: 0,
    withdrawals: 0,
    trades: 0,
    investments: 0
  });

  useEffect(() => {
    fetchTransactions();
    const interval = setInterval(fetchTransactions, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, filterType, filterDate, searchTerm, sortBy, sortOrder]);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/transactions/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTransactions(response.data.transactions || []);
        calculateStats(response.data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      if (error.response?.status === 401) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to view your transactions',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (txns) => {
    const newStats = {
      total: txns.length,
      deposits: txns.filter(t => {
        const type = t.type?.toUpperCase() || '';
        return type.includes('DEPOSIT') || type === 'DEPOSIT_REQUEST';
      }).length,
      withdrawals: txns.filter(t => {
        const type = t.type?.toUpperCase() || '';
        return type.includes('WITHDRAWAL') || type.includes('WITHDRAW');
      }).length,
      trades: txns.filter(t => {
        const type = t.type?.toLowerCase() || '';
        return type.includes('binary_trade') || type.includes('trade') || type.includes('conversion');
      }).length,
      investments: txns.filter(t => {
        const type = t.type?.toLowerCase() || '';
        return type.includes('arbitrage') || type.includes('investment');
      }).length
    };
    setStats(newStats);
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(t => getTransactionCategory(t.type) === filterType);
    }

    // Date filter
    if (filterDate !== 'all') {
      const now = new Date();
      const filterDays = {
        'today': 0,
        'week': 7,
        'month': 30,
        'quarter': 90
      };
      
      const days = filterDays[filterDate];
      if (days !== undefined) {
        const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(t => new Date(t.created_at) >= startDate);
      }
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.type_label?.toLowerCase().includes(search) ||
        t.currency?.toLowerCase().includes(search) ||
        t.transaction_id?.toLowerCase().includes(search) ||
        t.metadata?.pair?.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'date':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case 'amount':
          aVal = Math.abs(a.amount);
          bVal = Math.abs(b.amount);
          break;
        case 'type':
          aVal = a.type_label;
          bVal = b.type_label;
          break;
        default:
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredTransactions(filtered);
  };

  const handleExportCSV = () => {
    try {
      // Prepare CSV data
      const headers = ['Date', 'Type', 'Currency', 'Amount', 'Status', 'Description'];
      const csvData = filteredTransactions.map(tx => [
        new Date(tx.created_at || tx.date).toLocaleString(),
        tx.type_label || tx.type,
        tx.currency || 'USDT',
        tx.amount || 0,
        tx.status || 'completed',
        tx.description || ''
      ]);
      
      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      csvData.forEach(row => {
        csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
      });
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Export Successful',
        description: `Exported ${filteredTransactions.length} transactions to CSV`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to export transactions',
        variant: 'destructive'
      });
    }
  };

  const getTransactionCategory = (type) => {
    const typeUpper = (type || '').toUpperCase();
    const typeLower = (type || '').toLowerCase();
    
    // Binary trades
    if (typeUpper.includes('BINARY_TRADE') || typeLower.includes('binary_trade')) return 'binary_trades';
    
    // AI Arbitrage
    if (typeUpper.includes('ARBITRAGE') || typeLower.includes('arbitrage') || typeLower.includes('investment')) return 'ai_arbitrage';
    
    // Deposits
    if (typeUpper === 'DEPOSIT' || typeLower === 'deposit' || typeLower.includes('deposit')) return 'deposits';
    
    // Withdrawals
    if (typeUpper === 'WITHDRAWAL' || typeUpper === 'WITHDRAWAL_FEE' || typeLower.includes('withdrawal')) return 'withdrawals';
    
    // Conversions
    if (typeUpper === 'CRYPTO_CONVERSION' || typeLower === 'conversion' || typeLower.includes('conversion')) return 'conversions';
    
    // Admin actions
    if (typeUpper.includes('ADMIN') || typeLower.includes('admin')) return 'admin_actions';
    
    // Bonuses
    if (typeUpper.includes('BONUS') || typeLower.includes('bonus')) return 'bonuses';
    
    return 'other';
  };

  const getTransactionIcon = (type) => {
    const category = getTransactionCategory(type);
    const iconMap = {
      'binary_trades': Activity,
      'ai_arbitrage': TrendingUpDown,
      'deposits': ArrowDownCircle,
      'withdrawals': ArrowUpCircle,
      'conversions': RefreshCw,
      'admin_actions': AlertCircle,
      'bonuses': DollarSign,
      'other': FileText
    };
    return iconMap[category] || FileText;
  };

  const getTransactionColor = (type, amount) => {
    if (amount > 0) return 'text-green-400';
    if (amount < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const getTransactionBgColor = (type) => {
    const category = getTransactionCategory(type);
    const colorMap = {
      'binary_trades': 'bg-blue-500/10 border-blue-500/30',
      'ai_arbitrage': 'bg-purple-500/10 border-purple-500/30',
      'deposits': 'bg-green-500/10 border-green-500/30',
      'withdrawals': 'bg-orange-500/10 border-orange-500/30',
      'conversions': 'bg-cyan-500/10 border-cyan-500/30',
      'admin_actions': 'bg-yellow-500/10 border-yellow-500/30',
      'bonuses': 'bg-pink-500/10 border-pink-500/30',
      'other': 'bg-gray-500/10 border-gray-500/30'
    };
    return colorMap[category] || colorMap['other'];
  };

  const viewTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  const exportToCSV = () => {
    if (filteredTransactions.length === 0) {
      toast({
        title: 'No Data to Export',
        description: 'There are no transactions to export',
        variant: 'destructive'
      });
      return;
    }

    const csvData = filteredTransactions.map(t => ({
      'Date': t.created_at ? new Date(t.created_at).toLocaleString() : (t.date || 'N/A'),
      'Type': t.type_label || t.type || 'Unknown',
      'Amount': t.amount || 0,
      'Currency': t.currency || 'USDT',
      'Balance Before': t.balance_before || '',
      'Balance After': t.balance_after || '',
      'Status': t.status || 'completed',
      'Transaction ID': t.transaction_id || t.id || '',
      'Description': t.description || ''
    }));

    const headers = Object.keys(csvData[0]);
    const csv = [
      headers.join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: '✅ Export Complete',
      description: `Exported ${filteredTransactions.length} transactions to CSV`
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-cyan-500/30 p-6">
        <div className="flex items-center gap-3 mb-2">
          <Activity className="w-8 h-8 text-cyan-400" />
          <h1 className="text-3xl font-bold text-white">My Transaction History</h1>
        </div>
        <p className="text-cyan-400">View your personal transaction history and trading positions</p>
      </div>

      {/* Stats Cards */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-slate-800/50 border border-cyan-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-cyan-400 opacity-50" />
            </div>
          </div>
          <div className="bg-slate-800/50 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Deposits</p>
                <p className="text-2xl font-bold text-green-400">{stats.deposits}</p>
              </div>
              <ArrowDownCircle className="w-8 h-8 text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-slate-800/50 border border-orange-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Withdrawals</p>
                <p className="text-2xl font-bold text-orange-400">{stats.withdrawals}</p>
              </div>
              <ArrowUpCircle className="w-8 h-8 text-orange-400 opacity-50" />
            </div>
          </div>
          <div className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Trades</p>
                <p className="text-2xl font-bold text-blue-400">{stats.trades}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-slate-800/50 border border-purple-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Investments</p>
                <p className="text-2xl font-bold text-purple-400">{stats.investments}</p>
              </div>
              <TrendingUpDown className="w-8 h-8 text-purple-400 opacity-50" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-cyan-400" />
            <h2 className="text-xl font-bold text-white">Filter & Search</h2>
          </div>

          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by pair, type, or currency..."
                className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
              />
            </div>

            {/* Filter Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Transaction Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                >
                  <option value="all">All Types</option>
                  <option value="binary_trades">Binary Trades</option>
                  <option value="ai_arbitrage">AI Arbitrage</option>
                  <option value="deposits">Deposits</option>
                  <option value="withdrawals">Withdrawals</option>
                  <option value="conversions">Conversions</option>
                  <option value="admin_actions">Admin Actions</option>
                  <option value="bonuses">Bonuses</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Time Period</label>
                <select
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last 90 Days</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                >
                  <option value="date">Date</option>
                  <option value="amount">Amount</option>
                  <option value="type">Type</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterDate('all');
                  setSearchTerm('');
                  setSortBy('date');
                  setSortOrder('desc');
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Clear Filters
              </button>
              <button
                onClick={fetchTransactions}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/50 text-cyan-400 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={exportToCSV}
                disabled={filteredTransactions.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading transactions...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No transactions yet</h3>
              <p className="text-gray-400 mb-6">Your transaction history will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {filteredTransactions.map((transaction) => {
                const Icon = getTransactionIcon(transaction.type);
                const isCredit = transaction.amount > 0;
                
                return (
                  <div
                    key={transaction.transaction_id}
                    className={`p-4 hover:bg-slate-800/80 transition-colors cursor-pointer border-l-4 ${getTransactionBgColor(transaction.type)}`}
                    onClick={() => viewTransactionDetails(transaction)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`p-3 rounded-lg ${getTransactionBgColor(transaction.type)}`}>
                          <Icon className={`w-6 h-6 ${getTransactionColor(transaction.type, transaction.amount)}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-semibold">{transaction.type_label}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full ${transaction.status === 'completed' ? 'bg-green-500/20 text-green-400' : transaction.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                              {transaction.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400">
                            {formatDate(transaction.created_at)}
                          </p>
                          {transaction.metadata?.pair && (
                            <p className="text-xs text-cyan-400 mt-1">{transaction.metadata.pair}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${getTransactionColor(transaction.type, transaction.amount)}`}>
                          {isCredit ? '+' : ''}{transaction.amount.toFixed(2)} {(transaction.currency || '').toUpperCase()}
                        </div>
                        <div className="text-sm text-gray-400">
                          Balance: {transaction.balance_after?.toFixed(2)} {(transaction.currency || '').toUpperCase()}
                        </div>
                      </div>
                      <Eye className="w-5 h-5 text-gray-400 ml-4" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Transaction Detail Modal */}
        {showModal && selectedTransaction && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
            <div className="bg-slate-900 rounded-xl border border-cyan-500/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Transaction Details</h2>
                  <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Transaction Summary */}
                <div className="bg-gradient-to-br from-cyan-600/10 to-blue-600/10 border border-cyan-500/30 rounded-lg p-6 mb-6">
                  <div className="text-center mb-4">
                    <div className={`text-4xl font-bold ${getTransactionColor(selectedTransaction.type, selectedTransaction.amount)}`}>
                      {selectedTransaction.amount > 0 ? '+' : ''}{selectedTransaction.amount.toFixed(2)} {selectedTransaction.currency}
                    </div>
                    <div className="text-lg text-white mt-2">{selectedTransaction.type_label}</div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-400 mb-1">Transaction ID</div>
                      <div className="text-white font-mono text-xs break-all">{selectedTransaction.transaction_id}</div>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-400 mb-1">Status</div>
                      <div className={`font-semibold ${selectedTransaction.status === 'completed' ? 'text-green-400' : selectedTransaction.status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>
                        {selectedTransaction.status.toUpperCase()}
                      </div>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-400 mb-1">Date & Time</div>
                      <div className="text-white">{formatDate(selectedTransaction.created_at)}</div>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-400 mb-1">Currency</div>
                      <div className="text-white font-semibold">{(selectedTransaction.currency || '').toUpperCase()}</div>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-400 mb-1">Balance Before</div>
                      <div className="text-white">{selectedTransaction.balance_before?.toFixed(2)} {(selectedTransaction.currency || '').toUpperCase()}</div>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-400 mb-1">Balance After</div>
                      <div className="text-white">{selectedTransaction.balance_after?.toFixed(2)} {(selectedTransaction.currency || '').toUpperCase()}</div>
                    </div>
                  </div>

                  {/* Metadata */}
                  {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-400 mb-3">Additional Information</div>
                      <div className="space-y-2">
                        {Object.entries(selectedTransaction.metadata).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-gray-400">{key.replace(/_/g, ' ').toUpperCase()}:</span>
                            <span className="text-white font-semibold">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowModal(false)}
                  className="w-full mt-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default EnhancedTransactions;